import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Chat from '@mui/icons-material/Chat';
import AddIcon from '@mui/icons-material/Add';
import { ReactNode, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { CLIENT_CASES_CREATE } from 'src/constants/constants';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useAuth from 'src/hooks/useAuth';
import useHasPermissions from 'src/hooks/useHasPermission';
import { getAuthenticatedLandingPath } from 'src/utils/permissionLanding';
import useGenericStyles from 'src/styles/useGenericStyles';
import AccountPopover from './dashboard/AccountPopover';
import LinksWidget from './dashboard/LinksWidget';
import NotificationsWidget from './dashboard/NotificationsWidget';
import Logo from './Logo';
import { CustomButton2 } from './widgets/buttons/CustomButton2';

const navIconSx = {
  color: '#718096',
  borderRadius: '8px',
  transition: 'background-color 150ms ease, color 150ms ease',
  '&:hover': {
    backgroundColor: 'rgba(113, 128, 150, 0.12)',
    color: '#4A5568',
  },
};

interface HeaderProps {
  title?: string;
  children?: ReactNode;
}

export const Header = ({ title, children }: HeaderProps) => {
  const classes = useGenericStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const checkPermissions = useHasPermissions();
  const { userData, loadProfile } = useUserProfile();

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const userDisplayName = useMemo(() => {
    if (!userData) {
      return '';
    }

    const fromCamelCase = [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim();
    const fromSnakeCase = [userData.first_name, userData.last_name].filter(Boolean).join(' ').trim();

    return userData.name?.trim() || fromCamelCase || fromSnakeCase || userData.email || '';
  }, [userData]);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        height: '64px',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        left: 0,
        right: 0,
        top: 0,
        zIndex: 1000,
        boxShadow: 1,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title="Home" arrow>
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: '8px',
              px: 0.5,
              py: 0.25,
              transition: 'background-color 150ms ease',
              '&:hover': { backgroundColor: 'rgba(113, 128, 150, 0.08)' },
            }}
            onClick={() => navigate(getAuthenticatedLandingPath(user))}
          >
            <Logo height={32} />
            {/* <Logo width={60} height={60} /> */}
            <Box
              sx={{
                width: '1px',
                height: '40px', // adjust based on logo height
                backgroundColor: '#C4C4C4', // gray color
                marginLeft: 1,
                marginRight: 1,
              }}
            />

            {/* Placeholder for company name */}
            <Typography variant="h4" sx={{ fontSize: '16px', fontWeight: 600, opacity: 0.5 }} color="textSecondary">
              {userData?.organization?.name || userData?.organization?.organizationName || ' '}
            </Typography>
          </Box>
        </Tooltip>
        <Typography variant="h5" className={classes.headerHeading}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexDirection: 'row' }}>
        {checkPermissions([CLIENT_CASES_CREATE]) &&
          <Tooltip title="Create Ticket" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CustomButton2
                backgroundColor={theme.palette.primary.main}
                className={classes.button}
                onClick={() => navigate('/cases/add')}>
                <AddIcon
                  sx={{
                    marginRight: '5px',
                  }}
                />
                Create Ticket
              </CustomButton2>
            </Box>
          </Tooltip>
        }
        {children}
        <LinksWidget />
        <NotificationsWidget />
        <Tooltip title="Chat History" arrow>
          <IconButton
            size="small"
            onClick={() => navigate('/chats-history')}
            sx={navIconSx}
          >
            <Chat fontSize="small" />
          </IconButton>
        </Tooltip>
        <AccountPopover userDisplayName={userDisplayName} />

      </Box>
    </Box>
  );
};
